import { ReactRenderer } from "@tiptap/react"
import { actions } from "@fider/services"

import MentionList, { MentionItem, MentionListHandle } from "./MentionList"
import { MentionNodeAttrs } from "@tiptap/extension-mention"
interface MentionListProps {
  items: any[]
  command?: (item: MentionNodeAttrs) => void
}

// Tùy biến KHCN: danh sách người có thể nhắc phụ thuộc vào bài đang bình luận
// (tác giả + người đã bình luận). Cache ngắn để người vừa bình luận sớm xuất hiện.
const CACHE_TTL_MS = 20_000
const cache = new Map<string, { at: number; users: MentionItem[] }>()

const loadUsers = async (postNumber: number | undefined): Promise<MentionItem[]> => {
  const key = postNumber ? `post:${postNumber}` : "all"
  const cached = cache.get(key)
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
    return cached.users
  }
  const result = postNumber ? await actions.getPostTaggableUsers(postNumber) : await actions.getTaggableUsers("")
  const users: MentionItem[] = result.ok
    ? result.data.map((user, idx) => ({ id: String(user.id || idx), label: user.name, avatarURL: user.avatarURL, organization: user.organization }))
    : []
  cache.set(key, { at: Date.now(), users })
  return users
}

// Tùy biến KHCN: giữ danh sách gợi ý trong màn hình (điện thoại hẹp thì kéo vào lề phải)
const VIEWPORT_MARGIN = 8
const clampLeft = (left: number, container: HTMLElement): number => {
  const width = container.getBoundingClientRect().width || 280
  return Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN))
}

export const createMentionSuggestion = (getPostNumber: () => number | undefined) => ({
  items: async ({ query }: { query: string }) => {
    const users = await loadUsers(getPostNumber())
    const needle = query.toLowerCase()
    return users.filter((item) => item.label?.toLowerCase().includes(needle)).slice(0, 100)
  },
  render: () => {
    let reactRenderer: ReactRenderer<MentionListHandle, MentionListProps>
    let containerElement: HTMLElement | null = null
    let scrollListener: EventListener | null = null
    let clickOutsideListener: EventListener | null = null
    let initialPosition: { top: number; left: number } | null = null

    return {
      onStart: (props: { editor: any; clientRect?: (() => DOMRect | null) | null }) => {
        reactRenderer = new ReactRenderer(MentionList, {
          props,
          editor: props.editor,
        })

        if (!props.clientRect) {
          return
        }
        // Add click outside listener
        clickOutsideListener = (event: Event) => {
          if (event instanceof MouseEvent && containerElement && event.target instanceof Node && !containerElement.contains(event.target)) {
            // Click was outside the container, clean up
            if (scrollListener) {
              window.removeEventListener("scroll", scrollListener)
            }
            if (clickOutsideListener) {
              document.removeEventListener("click", clickOutsideListener)
            }
            if (containerElement && containerElement.parentNode) {
              document.body.removeChild(containerElement)
              containerElement = null
            }

            // Important: Return focus to editor
            props.editor.view.focus()
          }
        }
        // Use setTimeout to avoid immediate trigger when creating the popup
        setTimeout(() => {
          document.addEventListener("click", clickOutsideListener as EventListener)
        }, 100)

        // Create container for the suggestion list
        containerElement = document.createElement("div")
        containerElement.style.position = "absolute"
        containerElement.style.zIndex = "1000"
        document.body.appendChild(containerElement)
        containerElement.appendChild(reactRenderer.element)

        // Get initial position
        const rect = props.clientRect()
        if (rect) {
          // Store initial position relative to the document
          initialPosition = {
            top: rect.bottom + window.scrollY,
            left: clampLeft(rect.left, containerElement) + window.scrollX,
          }

          // Set initial position
          containerElement.style.left = `${initialPosition.left}px`
          containerElement.style.top = `${initialPosition.top}px`
        }

        // Add scroll listener to maintain position during scrolling
        scrollListener = () => {
          if (containerElement && initialPosition) {
            containerElement.style.top = `${initialPosition.top}px`
          }
        }

        window.addEventListener("scroll", scrollListener, { passive: true })
      },
      onUpdate(props: { clientRect?: (() => DOMRect | null) | null | undefined }) {
        if (!props.clientRect || !containerElement) {
          return
        }

        const rect = props.clientRect()
        if (!rect) {
          return
        }

        reactRenderer.updateProps(props)

        // Update position
        initialPosition = {
          top: rect.bottom + window.scrollY,
          left: clampLeft(rect.left, containerElement) + window.scrollX,
        }

        containerElement.style.left = `${initialPosition.left}px`
        containerElement.style.top = `${initialPosition.top}px`
      },

      onKeyDown(props: { event: KeyboardEvent }) {
        if (props.event.key === "Escape" && containerElement) {
          // Clean up
          if (scrollListener) {
            window.removeEventListener("scroll", scrollListener)
          }

          document.body.removeChild(containerElement)
          containerElement = null
          return true
        }

        return reactRenderer.ref?.onKeyDown(props) || false
      },

      onExit() {
        if (containerElement) {
          if (scrollListener) {
            window.removeEventListener("scroll", scrollListener)
          }
          if (clickOutsideListener) {
            document.removeEventListener("click", clickOutsideListener)
          }

          document.body.removeChild(containerElement)
          containerElement = null
        }

        reactRenderer.destroy()
      },
    }
  },
})

export default createMentionSuggestion(() => undefined)
